import { Link } from "react-router-dom";
import { PHONE_TEL, PHONE_DISPLAY } from "@/config/site";
import { SERVICE_CONTENT } from "@/config/serviceContent";
import { Phone, ArrowRight } from "lucide-react";

/**
 * NeedHelpBlock — internal-linking CTA row shown at the bottom of every
 * service and city page. Big SEO signal (cross-linked keyword-rich links)
 * and a real conversion lift (one-tap Call + related-service prompts).
 *
 * Props:
 *  - excludeSlug: string — the current service's slug (if we're on a service
 *                page) so we don't self-link
 *  - testId: string prefix for data-testids
 */
export default function NeedHelpBlock({ excludeSlug, testId = "need-help" }) {
  const links = [
    { slug: "jump-start", label: "Need a jump start?" },
    { slug: "roadside-assistance", label: "Need roadside assistance?" },
    { slug: "flatbed-towing", label: "Need flatbed towing?" },
    { slug: "car-lockout", label: "Locked out of your car?" },
    { slug: "tire-change", label: "Need a tire change?" },
    { slug: "emergency-towing", label: "24/7 emergency tow?" },
  ].filter((l) => l.slug !== excludeSlug);

  return (
    <section className="bg-yellow-400 text-slate-900 border-y-4 border-black" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl leading-tight">
            One call solves it. What do you need?
          </h2>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid={`${testId}-call`}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-yellow-400 font-display font-black uppercase tracking-wider px-6 py-3 border-2 border-black min-h-[48px]"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
          </a>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {links.map((l) => {
            const svc = SERVICE_CONTENT[l.slug];
            if (!svc) return null;
            return (
              <li key={l.slug}>
                <Link
                  to={svc.path}
                  data-testid={`${testId}-${l.slug}`}
                  className="flex items-center justify-between gap-3 bg-slate-950 hover:bg-black text-yellow-400 border-2 border-black px-4 py-3 font-display font-black uppercase tracking-wider text-sm min-h-[52px]"
                >
                  <span>{l.label}</span>
                  <ArrowRight className="h-4 w-4" strokeWidth={3} />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
